/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ContactDeliveryMethod,
  ContactDetailsDto,
  ContactType,
  JobOrderMethod,
  LocationDto,
} from '@gh/builder-shared-data';
import { EntityVersion, ID } from '@gh/common-shared-data';
import { OptimisticLockingSupport } from '@gh/core-data';
import { find, get } from 'lodash';
import { getContactName, getFullContactAddress } from '../../contact-details-util';
import { ContactDetailsCommon, } from './contact-details-common.model';
import {
  ContactDetailsCreditor,
  contactDetailsCreditorFromDto,
  contactDetailsCreditorToDto,
  getCreditorOriginalValue,
} from './contact-details-creditor.model';
import {
  ContactDetailsCustomer,
  contactDetailsCustomerFromDto,
  contactDetailsCustomerToDto
} from './contact-details-customer.model';
import { ContactDetailsDebtor } from './contact-details-debtor.model';
import {
  ContactDetailsEmployee,
  contactDetailsEmployeeFromDto,
  contactDetailsEmployeeToDto,
} from './contact-details-employee.model';
import { ContactDetailsOther } from './contact-details-other.model';

export type ContactLocation = LocationDto;

export class ContactDetails implements OptimisticLockingSupport {
  constructor(public common: ContactDetailsCommon,
              public employee?: ContactDetailsEmployee,
              public creditorSupplier?: ContactDetailsCreditor,
              public customer?: ContactDetailsCustomer,
              public debtor?: ContactDetailsDebtor,
              public other?: ContactDetailsOther,
              public hashedId?: string,
              public id?: ID,
              public etag?: string,
              public version?: EntityVersion) {
  }
}

export function hasContactType(model: ContactDetails, type: ContactType): boolean {
  return get(find(model.common.contactTypes, { type }), 'assigned', false);
}

export function contactDetailsFromDto(dto: ContactDetailsDto): ContactDetails {
  return {
    ...dto,
    employee: dto.employee ? contactDetailsEmployeeFromDto(dto.employee) : void 0,
    creditorSupplier: dto.creditorSupplier ? contactDetailsCreditorFromDto(dto.creditorSupplier, dto) : void 0,
    customer: dto.customer ? contactDetailsCustomerFromDto(dto.customer) : void 0,
  };
}

export function contactDetailsToDto(model: ContactDetails): ContactDetailsDto {
  return {
    ...model,
    customer: model.customer ? contactDetailsCustomerToDto(model.customer) : void 0,
    employee: model.employee ? contactDetailsEmployeeToDto(model.employee) : void 0,
    creditorSupplier: model.creditorSupplier ? contactDetailsCreditorToDto(model.creditorSupplier) : void 0,
  };
}

export function getContactTabOriginalValue(current: ContactDetails,
                                           original: ContactDetails,
                                           name: keyof ContactDetails): any {
  if (name === 'creditorSupplier') {
    return getCreditorOriginalValue(current, original);
  } else {
    return original[name];
  }
}

export function extractRequestedInfo(contactId: ID,
                                     version: EntityVersion,
                                     details: ContactDetails,
                                     entryPoint: string): object {
  switch (entryPoint) {
    case 'lead': {
      const customer = details.customer!; // tslint:disable-line:no-non-null-assertion
      const referredBy = customer.common.referredBy.referredBy;

      return {
        contactId,
        version,
        name: getContactName(details.common),
        referredBy: {
          code: 'REFERRED_BY',
          itemCode: referredBy.id,
          localization: referredBy.name,
        },
        details: customer.common.referredBy.referredDetails,
        community: customer.common.referredBy.community,
        address: getFullContactAddress(details.common),
        phone: details.common.phone1 || details.common.phone2,
        email: customer.common.billing.isBillingSame ?
          details.common.email
          : customer.common.billing.email || details.common.email,
      };
    }
    case 'quick-job':
      return {
        contactId,
        name: getContactName(details.common),
        addressee: details.common.addressee,
        version,
      };
    case 'job-order':
      return {
        contactId,
        name: getContactName(details.common),
        orderBy: details.creditorSupplier && {
          [ContactDeliveryMethod.Email]: JobOrderMethod.Email,
          [ContactDeliveryMethod.Mail]: JobOrderMethod.Printer,
        }[details.creditorSupplier.common.orderBy.deliveryMethod],
        version,
      };
    case 'user':
      return {
        contactId,
        firstName: get(details.common, 'individualName.firstName'),
        lastName: get(details.common, 'individualName.lastName'),
        name: getContactName(details.common),
        types: details.common.contactTypes.filter((type) => type.assigned).map((type) => type.type),
      };
    default:
      return {
        contactId,
        name: getContactName(details.common),
        types: details.common.contactTypes.filter((type) => type.assigned).map((type) => type.type),
      };
  }
}
